import { type ElementType, type ReactNode } from 'react';
import type { GridExceptionKey } from '@icebreaker/shared/design';
import styles from './list-surface.module.css';

/**
 * LİSTE ANATOMİLERİ — Revizyon 2 §4.
 *
 * `stack-cover`  Yığın · kapaklı (D varyantı). Kapak 16:9 · açıklama 2 satır ·
 *                konuşmacı satırı · gerekçe · avatar yığını · eylem.
 *                → etkinlik keşfi · öneriler · kişisel ana sayfa önerilen
 *                  etkinlikler · public etkinlik listesi
 * `stack-plain`  Yığın · kapaksız (B/A). Kişi öğesi: avatar 48 · gerekçe
 *                bloğu · eylem alt satırda. Kapak yok — görsel avatardır.
 *                → önerilen kişiler · kişi kartı · katılımcı keşfi · davet ve
 *                  başvuru ekranları
 * `row`          Satır / tablo. Toplu tarama; xxl'de ızgaraya GEÇMEZ.
 *                → kayıtlar · check-in · katılımcı dizini · cüzdan · davetler ·
 *                  başvurular · ekip üyeleri · admin listeleri
 * `timeline`     Zaman çizgisi. Okuma rayı 880 sola yaslı, saat kolonu 80 sabit.
 *                → Ajandam · etkinlik ajandası
 */
export type ListAnatomy = 'stack-cover' | 'stack-plain' | 'row' | 'timeline';

export interface ListSurfaceProps {
  readonly anatomy: ListAnatomy;
  /** Sayfanın kendi liste sınıfı — İÇ kutuya biner (dikey ritim, kart stili). */
  readonly className?: string;
  /** DIŞ kutu (konteyner) etiketi. */
  readonly as?: ElementType;
  /**
   * İÇ kutu (ızgara) etiketi. Konteyner sorgusu öğenin KENDİSİNİ sorgulayamaz,
   * bu yüzden ızgara her zaman konteynerin ALTINDA ayrı bir öğedir. Anlamsal
   * liste gerektiğinde (`ul`/`ol`) burada verilir — `li` çocukları geçerli
   * kalsın diye kap `div`e SABİTLENMEZ.
   */
  readonly listAs?: ElementType;
  /**
   * IZGARA TAVANI İSTİSNASI — `gridExceptions` kaydındaki bir anahtar
   * (`@icebreaker/shared/design`). Yalnız yığın anatomilerinde anlamlıdır;
   * ızgara kuralı `layoutLadderCssVariables()` içinde `[data-grid-exception]`
   * seçicisiyle üretilir. Kayıtsız anahtar TİPTE reddedilir; kullanılmayan
   * kayıt kapıda (`list-anatomy.guard.spec`) bayat sayılır.
   */
  readonly gridException?: GridExceptionKey;
  readonly id?: string;
  readonly role?: string;
  readonly 'aria-label'?: string;
  readonly 'aria-busy'?: boolean;
  readonly children: ReactNode;
}

/** Yığın anatomileri ızgaraya açılır; satır ve zaman çizgisi AÇILMAZ. */
const OPENS_TO_GRID: Record<ListAnatomy, boolean> = {
  'stack-cover': true,
  'stack-plain': true,
  row: false,
  timeline: false,
};

const INNER_CLASS: Record<ListAnatomy, string> = {
  'stack-cover': styles.stack ?? '',
  'stack-plain': styles.stack ?? '',
  row: styles.rows ?? '',
  timeline: styles.timeline ?? '',
};

/**
 * TEK LİSTE KABI. Anatomiyi tek yerde sahiplenir; ekran yalnız hangi anatomide
 * olduğunu BİLDİRİR.
 *
 * Dış kutu konteynerdir (`container-type: inline-size`) — ızgara eşiği ekrana
 * değil ORTA KOLONA bağlıdır. `ib-stack-grid` genel sınıfı yalnız yığın
 * anatomilerine biner; ızgarayı açan konteyner sorgusu ona bakar.
 *
 * Kalıcı kontrol: `page-shell-rail.guard.spec.ts` depoyu `git ls-files` ile
 * tarar. `ListSurface` kullanan her dosya anatomi kayıtında OLMAK zorundadır;
 * kayıtta yazandan başka anatomi kullanırsa test KIRILIR.
 */
export function ListSurface({
  anatomy,
  className,
  as,
  listAs,
  gridException,
  id,
  role,
  children,
  ...aria
}: ListSurfaceProps) {
  const Outer: ElementType = as ?? 'div';
  const Inner: ElementType = listAs ?? 'div';
  const inner = [
    INNER_CLASS[anatomy],
    OPENS_TO_GRID[anatomy] ? 'ib-stack-grid' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Outer className={styles.surface} data-anatomy={anatomy} id={id}>
      <Inner
        className={inner}
        role={role}
        data-grid-exception={OPENS_TO_GRID[anatomy] ? gridException : undefined}
        {...aria}
      >
        {children}
      </Inner>
    </Outer>
  );
}
